import styled, { createGlobalStyle } from 'styled-components';

export const GlobalStyle = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
    background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 50%, #90caf9 100%);
    min-height: 100vh;
    color: #0d47a1;
    line-height: 1.6;
  }

  html {
    scroll-behavior: smooth;
  }

  a {
    text-decoration: none;
    color: inherit;
  }

  button {
    cursor: pointer;
    border: none;
    font-family: inherit;
  }

  input, textarea, select {
    font-family: inherit;
  }

  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
  }

  .section-padding {
    padding: 80px 0;
  }

  @media (max-width: 1024px) {
    body {
      padding-bottom: 70px; /* Account for mobile navigation bar */
    }
  }

  @media (max-width: 768px) {
    .section-padding {
      padding: 60px 0;
    }
  }

  @media (max-width: 480px) {
    body {
      padding-bottom: 65px; /* Smaller mobile nav height */
    }
  }
    
    .container {
      padding: 0 15px;
    }
  }
`;

export const theme = {
  colors: {
    primary: '#006994',
    primaryLight: '#42a5f5',
    primaryDark: '#0d47a1',
    secondary: '#00bcd4',
    secondaryLight: '#4dd0e1',
    accent: '#ff6b35',
    background: '#f8fdff',
    backgroundSecondary: '#e3f2fd',
    white: '#ffffff',
    gray: '#64748b',
    grayLight: '#94a3b8',
    grayDark: '#334155',
    text: '#1e293b',
    textLight: '#64748b',
    success: '#10b981',
    warning: '#f59e0b',
    error: '#ef4444',
    coral: '#ff7875',
    coralLight: '#ffa39e',
    ocean: '#0077be',
    wave: '#87ceeb'
  },
  gradients: {
    ocean: 'linear-gradient(135deg, #006994 0%, #00bcd4 100%)',
    wave: 'linear-gradient(45deg, #42a5f5 0%, #4dd0e1 100%)',
    coral: 'linear-gradient(45deg, #ff7875 0%, #ffa39e 100%)',
    background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 50%, #90caf9 100%)',
    card: 'linear-gradient(145deg, #ffffff 0%, #f8fdff 100%)',
    button: 'linear-gradient(45deg, #006994 0%, #0077be 100%)'
  },
  shadows: {
    card: '0 4px 20px rgba(0, 105, 148, 0.15)',
    cardHover: '0 8px 30px rgba(0, 105, 148, 0.25)',
    button: '0 4px 15px rgba(0, 105, 148, 0.3)',
    ocean: '0 10px 40px rgba(0, 188, 212, 0.3)'
  },
  borderRadius: {
    small: '8px',
    medium: '12px',
    large: '16px',
    xl: '24px'
  },
  breakpoints: {
    mobile: '480px',
    tablet: '768px',
    desktop: '1024px',
    wide: '1200px'
  }
};

export const Card = styled.div`
  background: ${props => props.theme.gradients.card};
  border-radius: ${props => props.theme.borderRadius.large};
  box-shadow: ${props => props.theme.shadows.card};
  padding: 2rem;
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.2);

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${props => props.theme.shadows.cardHover};
  }

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    padding: 1.5rem;
  }
`;

export const Button = styled.button.withConfig({
  shouldForwardProp: (prop) => !['secondary', 'outline', 'size'].includes(prop),
})`
  background: ${props => props.secondary ? 
    props.theme.gradients.wave : 
    props.theme.gradients.button};
  color: white;
  padding: 14px 28px;
  border-radius: ${props => props.theme.borderRadius.medium};
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.3s ease;
  box-shadow: ${props => props.theme.shadows.button};
  border: none;
  cursor: pointer;
  text-transform: none;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 105, 148, 0.4);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  ${props => props.outline && `
    background: transparent;
    color: ${props.theme.colors.primary};
    border: 2px solid ${props.theme.colors.primary};
    box-shadow: none;

    &:hover {
      background: ${props.theme.colors.primary};
      color: white;
    }
  `}

  ${props => props.size === 'large' && `
    padding: 18px 36px;
    font-size: 1.1rem;
  `}

  ${props => props.size === 'small' && `
    padding: 10px 20px;
    font-size: 0.9rem;
  `}
`;

export const Input = styled.input`
  width: 100%;
  padding: 14px 16px;
  border: 2px solid #e2e8f0;
  border-radius: ${props => props.theme.borderRadius.medium};
  font-size: 1rem;
  transition: all 0.3s ease;
  background: white;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(0, 105, 148, 0.1);
  }

  &::placeholder {
    color: ${props => props.theme.colors.grayLight};
  }
`;

export const TextArea = styled.textarea`
  width: 100%;
  padding: 14px 16px;
  border: 2px solid #e2e8f0;
  border-radius: ${props => props.theme.borderRadius.medium};
  font-size: 1rem;
  transition: all 0.3s ease;
  background: white;
  resize: vertical;
  min-height: 120px;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(0, 105, 148, 0.1);
  }

  &::placeholder {
    color: ${props => props.theme.colors.grayLight};
  }
`;

export const Select = styled.select`
  width: 100%;
  padding: 14px 16px;
  border: 2px solid #e2e8f0;
  border-radius: ${props => props.theme.borderRadius.medium};
  font-size: 1rem;
  transition: all 0.3s ease;
  background: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(0, 105, 148, 0.1);
  }
`;

export const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 12px;
  border-radius: ${props => props.theme.borderRadius.medium};
  font-size: 0.85rem;
  font-weight: 600;
  white-space: nowrap;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: none;
  vertical-align: top;
  line-height: 1.2;
  min-height: 28px;
  box-sizing: border-box;
  
  background: ${props => {
    switch(props.variant) {
      case 'success': return 'linear-gradient(135deg, #10b981 0%, #059669 100%)';
      case 'warning': return 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)';
      case 'error': return 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)';
      case 'info': return 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)';
      case 'coral': return 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)';
      case 'ocean': return 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)';
      case 'purple': return 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)';
      case 'teal': return 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)';
      case 'indigo': return 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)';
      case 'pink': return 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)';
      case 'emerald': return 'linear-gradient(135deg, #10b981 0%, #047857 100%)';
      case 'amber': return 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)';
      case 'slate': return 'linear-gradient(135deg, #64748b 0%, #475569 100%)';
      case 'neutral': return 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)';
      case 'lime': return 'linear-gradient(135deg, #84cc16 0%, #65a30d 100%)';
      case 'cyan': return 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)';
      case 'rose': return 'linear-gradient(135deg, #f43f5e 0%, #e11d48 100%)';
      case 'violet': return 'linear-gradient(135deg, #a855f7 0%, #9333ea 100%)';
      case 'orange': return 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)';
      case 'sky': return 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)';
      case 'green': return 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)';
      case 'red': return 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)';
      case 'blue': return 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)';
      case 'yellow': return 'linear-gradient(135deg, #eab308 0%, #ca8a04 100%)';
      case 'fuchsia': return 'linear-gradient(135deg, #d946ef 0%, #c026d3 100%)';
      default: return 'linear-gradient(135deg, #42a5f5 0%, #1976d2 100%)';
    }
  }};
  
  color: ${props => {
    switch(props.variant) {
      case 'neutral': return '#374151';
      default: return 'white';
    }
  }};
  
  text-transform: ${props => props.noTransform ? 'none' : 'capitalize'};
  letter-spacing: 0.3px;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }
`;

export const WaveBackground = styled.div`
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1200 120'%3E%3Cpath d='M0,60 C150,100 350,0 600,60 C850,120 1050,20 1200,60 L1200,120 L0,120 Z' fill='%2342a5f5' fill-opacity='0.1'/%3E%3C/svg%3E") no-repeat;
    background-size: cover;
    background-position: bottom;
    pointer-events: none;
  }
`;

